import * as Schema from "effect/Schema";
import { NonNegativeInt, ThreadId, TrimmedNonEmptyString } from "./baseSchemas.ts";

const FILESYSTEM_PATH_MAX_LENGTH = 512;

export const FilesystemBrowseInput = Schema.Struct({
  partialPath: TrimmedNonEmptyString.check(Schema.isMaxLength(FILESYSTEM_PATH_MAX_LENGTH)),
  cwd: Schema.optional(TrimmedNonEmptyString.check(Schema.isMaxLength(FILESYSTEM_PATH_MAX_LENGTH))),
});
export type FilesystemBrowseInput = typeof FilesystemBrowseInput.Type;

export const FilesystemBrowseEntry = Schema.Struct({
  name: TrimmedNonEmptyString,
  fullPath: TrimmedNonEmptyString,
});
export type FilesystemBrowseEntry = typeof FilesystemBrowseEntry.Type;

export const FilesystemBrowseResult = Schema.Struct({
  parentPath: TrimmedNonEmptyString,
  entries: Schema.Array(FilesystemBrowseEntry),
});
export type FilesystemBrowseResult = typeof FilesystemBrowseResult.Type;

export const FilesystemBrowseFailure = Schema.Literals([
  "windows_path_unsupported",
  "current_project_required",
  "read_directory_failed",
  // This Desk has this Mac's Messages off (CLEO_DISABLE_HOST_MESSAGES): the folder is in a Messages or Contacts store.
  "host_messages_off",
]);
export type FilesystemBrowseFailure = typeof FilesystemBrowseFailure.Type;

function decodedFilesystemBrowseErrorMessage(props: object): string | undefined {
  if (!("message" in props)) return undefined;
  return typeof props.message === "string" ? props.message : undefined;
}

export class FilesystemBrowseError extends Schema.TaggedErrorClass<FilesystemBrowseError>()(
  "FilesystemBrowseError",
  {
    partialPath: Schema.optional(TrimmedNonEmptyString),
    cwd: Schema.optional(TrimmedNonEmptyString),
    failure: Schema.optional(FilesystemBrowseFailure),
    parentPath: Schema.optional(TrimmedNonEmptyString),
    platform: Schema.optional(TrimmedNonEmptyString),
    message: TrimmedNonEmptyString,
    cause: Schema.optional(Schema.Defect()),
  },
) {
  // Structured diagnostics stay optional for rolling compatibility with legacy message-only
  // payloads, while new call sites must provide the request context and failure classification.
  // @effect-diagnostics-next-line overriddenSchemaConstructor:off
  constructor(props: {
    readonly partialPath: string;
    readonly cwd?: string | undefined;
    readonly failure: FilesystemBrowseFailure;
    /** The refusal's own sentence, when it has one; otherwise a generic message. */
    readonly message?: string;
    readonly parentPath?: string;
    readonly platform?: string;
    readonly cause?: unknown;
  }) {
    const cwd = props.cwd === undefined ? "" : ` from '${props.cwd}'`;
    super({
      ...props,
      message:
        decodedFilesystemBrowseErrorMessage(props) ??
        `Failed to browse filesystem path '${props.partialPath}'${cwd}.`,
    } as any);
  }
}

// ---------------------------------------------------------------------------
// UP9: folders listed on demand, and host files read through a governed reader.
//
// A folder listing answers one folder's immediate children — every child, the ignored ones
// marked rather than hidden, and no workspace-wide cap, because the reader opens folders one at
// a time. A host file outside the workspace is read only through the session's message
// boundary: the reader names the session, the server checks the path against the roots that
// session admitted, and the answer is read-only.
// ---------------------------------------------------------------------------

const HOST_FILE_PATH_MAX_LENGTH = 2048;

export const WorkspaceDirectoryEntryKind = Schema.Literals([
  "file",
  "directory",
  "symlink",
  "other",
]);
export type WorkspaceDirectoryEntryKind = typeof WorkspaceDirectoryEntryKind.Type;

export const WorkspaceDirectoryEntry = Schema.Struct({
  name: TrimmedNonEmptyString,
  /** Workspace-relative, forward slashes. */
  path: TrimmedNonEmptyString,
  kind: WorkspaceDirectoryEntryKind,
  /**
   * Excluded by the repository's ignore rules, or the repository's own store. Shown dimmed and
   * still browsable — never hidden.
   */
  ignored: Schema.Boolean,
});
export type WorkspaceDirectoryEntry = typeof WorkspaceDirectoryEntry.Type;

export const WorkspaceDirectoryInput = Schema.Struct({
  cwd: TrimmedNonEmptyString.check(Schema.isMaxLength(FILESYSTEM_PATH_MAX_LENGTH)),
  /** The folder to list, relative to the workspace root; absent, "" or "." is the root itself. */
  relativePath: Schema.optional(
    Schema.String.check(Schema.isMaxLength(FILESYSTEM_PATH_MAX_LENGTH)),
  ),
});
export type WorkspaceDirectoryInput = typeof WorkspaceDirectoryInput.Type;

export const WorkspaceDirectoryResult = Schema.Struct({
  /** "" for the root. */
  relativePath: Schema.String,
  /** Whether the folder itself is ignored; then every child is too. */
  ignored: Schema.Boolean,
  /** Folders first, then files, each in locale order. Never truncated. */
  entries: Schema.Array(WorkspaceDirectoryEntry),
});
export type WorkspaceDirectoryResult = typeof WorkspaceDirectoryResult.Type;

export const HostFileReadInput = Schema.Struct({
  threadId: ThreadId,
  absolutePath: TrimmedNonEmptyString.check(Schema.isMaxLength(HOST_FILE_PATH_MAX_LENGTH)),
});
export type HostFileReadInput = typeof HostFileReadInput.Type;

export const HostFileReadResult = Schema.Struct({
  absolutePath: TrimmedNonEmptyString,
  /** The admitted root of the session's boundary that the file lies in. */
  admittedRoot: TrimmedNonEmptyString,
  contents: Schema.String,
  byteLength: NonNegativeInt,
  truncated: Schema.Boolean,
  /** A host file is never written from here. */
  readOnly: Schema.Literal(true),
});
export type HostFileReadResult = typeof HostFileReadResult.Type;

/** The one door for both: a tagged request on one route. */
export const WorkspaceFileSystemRequest = Schema.Union([
  Schema.TaggedStruct("directory", WorkspaceDirectoryInput.fields),
  Schema.TaggedStruct("host-file", HostFileReadInput.fields),
]);
export type WorkspaceFileSystemRequest = typeof WorkspaceFileSystemRequest.Type;

export const WORKSPACE_FILE_SYSTEM_ROUTE = "/api/workspace/files";

/** `/…` on POSIX, `C:\…` or `C:/…` on Windows: a path naming a host location, not a workspace member. */
export function isAbsoluteHostPath(path: string): boolean {
  return path.startsWith("/") || /^[A-Za-z]:[\\/]/.test(path);
}

interface NormalizedHostPath {
  /** Lower-cased drive with its colon on Windows, "" elsewhere. */
  readonly drive: string;
  readonly segments: ReadonlyArray<string>;
}

function normalizeHostPath(path: string): NormalizedHostPath | null {
  if (!isAbsoluteHostPath(path)) return null;
  const windows = /^[A-Za-z]:[\\/]/.test(path);
  const drive = windows ? path.slice(0, 2).toLowerCase() : "";
  const segments: string[] = [];
  for (const segment of (windows ? path.slice(2) : path).split(/[\\/]+/)) {
    if (segment === "" || segment === ".") continue;
    if (segment === "..") {
      segments.pop();
      continue;
    }
    segments.push(segment);
  }
  return { drive, segments };
}

const sameSegment = (drive: string, left: string, right: string): boolean =>
  drive === "" ? left === right : left.toLowerCase() === right.toLowerCase();

/**
 * Whether `candidate` is `root` or lies beneath it, comparing normalized segments so `a/b/../c`
 * and a trailing separator do not fool the check. Lexical only: the server pairs it with a
 * realpath check so a symlink cannot cross it.
 */
export function isHostPathWithin(root: string, candidate: string): boolean {
  const base = normalizeHostPath(root);
  const target = normalizeHostPath(candidate);
  if (base === null || target === null || base.drive !== target.drive) return false;
  if (target.segments.length < base.segments.length) return false;
  return base.segments.every((segment, index) =>
    sameSegment(base.drive, segment, target.segments[index]!),
  );
}

/**
 * `candidate` relative to `root` with forward slashes, "" when they are the same place, and null
 * when `candidate` is not within `root`.
 */
export function hostPathRelativeTo(root: string, candidate: string): string | null {
  if (!isHostPathWithin(root, candidate)) return null;
  const base = normalizeHostPath(root)!;
  const target = normalizeHostPath(candidate)!;
  return target.segments.slice(base.segments.length).join("/");
}
